import React, {useCallback, useContext, useMemo} from "react";
import {Tooltip} from "antd";
import {flatten, uniq} from "lodash";
import {QuestionCircleOutlined} from "@ant-design/icons";
import {CheckboxController} from "./PaymentInputHelper";
import {MarketplaceContext} from "../../marketplaceContext";
import {PaymentType, ServiceIdToName} from "../../../consts.js";
import {AppContext} from "../../../AppContext";
import {SortableList} from "../../../components";
import {getTotalBudget} from "../../../utils/calculationUtils";
import {CompanyBudgetLabel} from "../../../company/CompanyBudgetLabel.jsx";
import {StringBuilder} from "../../../AppUtils.js";
import {StatisticsLabel} from "../../../admin/components/StatisticsLabel.jsx";
import classnames from "classnames";
import "./budget-payment-input.css";
import {NOOP} from "../../../utils/NOOP.jsx";

const BudgetPaymentInput = ({
    paymentType,
    setPaymentType,
    budgetsPriority,
    onBudgetCategoriesPriorityChange,
    budgetLoadRequestAmount,
    log = NOOP,
    me = {},
    company = {},
    services = [],
    onSyncUserBudget = NOOP
}) => {
    const {product, productBundle, isProductBundle} = useContext(MarketplaceContext);

    const productServices = useMemo(() => {
        if (isProductBundle) {
            return uniq(flatten(productBundle.products.map(({services}) => services)));
        }

        return product?.services;
    }, [product, productBundle, isProductBundle]);

    const totalBudget = useMemo(() => getTotalBudget(me?.budget, productServices), [me?.budget, productServices]);

    const onSortEnd = useCallback(
        ({oldIndex, newIndex}) => {
            const newSubCategories = [...budgetsPriority];
            newSubCategories.splice(newIndex, 0, newSubCategories.splice(oldIndex, 1)[0]);
            onBudgetCategoriesPriorityChange(newSubCategories);
        },
        [budgetsPriority, onBudgetCategoriesPriorityChange]
    );

    return (
        <div className={classnames("payment-input-container", "budget-payment-input-container")}>
            <div className={"payment-input-options-header"}>
                <CheckboxController
                    isChecked={paymentType === PaymentType.Budget}
                    setPaymentType={type => setPaymentType(type)}
                    type={PaymentType.Budget}
                    isDisabled={!me.features?.company_master_budget_load}
                />
                תשלום באמצעות תקציב
                <Tooltip
                    title={
                        <p className="payment-input-tooltip-text">
                            בחירה באפשרות תשלום באמצעות תקציב מאפשרת לכם לשלם על ההזמנה באמצעות הסכום שנטען.
                        </p>
                    }>
                    <QuestionCircleOutlined className="payment-input-tooltip-icon" />
                </Tooltip>
            </div>

            <div className="budget-payment-input-details">
                <StatisticsLabel
                    trigger={["hover"]}
                    dropdownClassName="current-total-budget"
                    title={
                        <>
                            <div className="total-budget">
                                {new StringBuilder("יתרת התקציב בחשבון זה")
                                    .append(`${(totalBudget ?? 0)?.toLocaleString()} ₪`)
                                    .toString()}
                            </div>
                            <div className="budget-description">
                                {budgetLoadRequestAmount > 0
                                    ? "לצורך ביצוע הזמנה אנא השלימו לסכום העסקה"
                                    : "ניתן לבצע את העסקה"}
                            </div>
                        </>
                    }
                />
                {budgetLoadRequestAmount > 0 && me?.features?.order_form_budget_load_btn ? (
                    <CompanyBudgetLabel
                        forceShowLoadButton={true}
                        loadButtonText={new StringBuilder("טעינת תקציב של")
                            .append(`₪${Number(budgetLoadRequestAmount).toLocaleString()}`)
                            .toString()}
                        budgetLoadRequestAmount={budgetLoadRequestAmount}
                        log={log}
                        me={me}
                        company={company}
                        services={services}
                        onSyncUserBudget={onSyncUserBudget}
                    />
                ) : null}
            </div>

            {me.features?.category_budget && paymentType === PaymentType.Budget && budgetsPriority?.length > 1 ? (
                <div>
                    <div className="payment-input-budgets-list-container">
                        סדר עדיפות למימוש תקציב
                        <QuestionCircleOutlined />
                    </div>
                    <SortableList
                        className="payment-input-budgets-list"
                        itemClassName="payment-input-budgets-list-item"
                        onSortEnd={onSortEnd}>
                        {budgetsPriority?.map((categoryId, index) => {
                            return (
                                <div key={categoryId}>
                                    {`${index + 1}. תקציב ${ServiceIdToName[categoryId] ?? "צף"}: ${me.budget[
                                        categoryId ?? "amount"
                                    ]?.toLocaleString()}`}
                                </div>
                            );
                        })}
                    </SortableList>
                </div>
            ) : null}
        </div>
    );
};

export default BudgetPaymentInput;
